import { PERSON_CARD_CAPS } from './person-card';

// One edit from the person card, applied to a Person's structured values (plan P1.16, P1.4) or to the stated half of
// their relationship profile (plan P2.3).
//
// THE CONTRACT is native-apps/shared/people-import/card-edit-contract.md — the one the iOS and Android cards send
// to the native-data `edit-person-card` action, whose server is
// packages/ai-scheduling-assistant-server/src/endpoints/handlers/personCardEdit.ts. Web's `/api/contact-person`
// takes the SAME body and answers the SAME response and error codes, and this file is a line-for-line port of that
// server's parse / apply / run so the three surfaces cannot accept, match or refuse differently:
//
//   request   { personId, edit: { op: 'set' | 'forget', field, value?, target? } }
//   200       { ok: true, changed, contact }      — contact: the person's ListContacts entry, re-read after the write
//   4xx       { error, max? }  (+ web's `messageKey`, the catalog key the card shows — an API route ships keys)
//
// Pure, with no runtime import of common-lib: web jest cannot load that graph (orion-web CLAUDE.md, "Tests").
// Every value the edit writes is the user's own — provenance { source: USER, confidence: STATED }; `setAt` is the
// service's. A removed value becomes a tombstone server-side (P1.3/P1.4), so a sync does not refill it.

export type CardField = 'relationship' | 'importantDate' | 'socialProfile' | 'organization' | 'relationshipProfile';

interface Provenance {
  source?: string;
  confidence?: string;
  sourceUrl?: string;
  setAt?: unknown;
}
export interface StoredRelationship {
  relation?: string;
  relatedToPersonId?: string;
  provenance?: Provenance;
  derived?: unknown;
}
export interface StoredImportantDate {
  kind?: string;
  calendar?: string;
  month?: number;
  day?: number;
  year?: number;
  hebrewMonth?: string;
  label?: string;
  provenance?: Provenance;
}
export interface StoredSocialProfile {
  network?: string;
  handle?: string;
  url?: string;
  provenance?: Provenance;
}
export interface StoredOrganization {
  company?: string;
  title?: string;
  website?: string;
  provenance?: Provenance;
}

/** The stated half of a person's relationship profile (plan P2.3), as People stores it (enums by name). */
export interface StoredStatedProfile {
  feeling?: string;
  feelingWords?: string;
  closeness?: string;
  register?: string;
  toneNote?: string;
  address?: string;
  avoid?: string[];
  doNotApproach?: boolean;
  provenance?: Provenance;
}

/** A field of the stated relationship profile the card writes. */
export type StatedProfileField = Exclude<keyof StoredStatedProfile, 'provenance'>;
/**
 * A `set` of the stated relationship profile: only the keys present are written. A key mapped to `undefined` is one
 * the card sent EMPTY (`""`, `[]`, `false`) — written as cleared.
 */
export type StatedProfileSet = Partial<Record<StatedProfileField, string | string[] | boolean | undefined>>;
/** What a relationship-profile `forget` names: one row of the card's "How you relate" section. */
export type RelationshipProfileSlot = 'feeling' | 'closeness' | 'tone' | 'address' | 'avoid' | 'doNotApproach';

/** The structured values of a Person, as the People service stores them (enums as their string values). */
export interface PersonCardValues {
  relationships?: StoredRelationship[];
  importantDates?: StoredImportantDate[];
  socialProfiles?: StoredSocialProfile[];
  organization?: StoredOrganization;
  /** Only on the one read that carries it (GetPerson with `includeRelationshipProfile`); the learned half is read-only. */
  relationshipProfile?: { stated?: StoredStatedProfile; learned?: unknown };
}
export type StoredPerson = PersonCardValues & { id?: string; revision?: string };

export type CardEdit =
  | { op: 'set'; field: 'relationship'; value: StoredRelationship; target?: StoredRelationship }
  | { op: 'set'; field: 'importantDate'; value: StoredImportantDate; target?: StoredImportantDate }
  | { op: 'set'; field: 'socialProfile'; value: StoredSocialProfile; target?: StoredSocialProfile }
  | { op: 'set'; field: 'organization'; value: StoredOrganization }
  | { op: 'forget'; field: 'relationship'; target: StoredRelationship }
  | { op: 'forget'; field: 'importantDate'; target: StoredImportantDate }
  | { op: 'forget'; field: 'socialProfile'; target: StoredSocialProfile }
  | { op: 'forget'; field: 'organization' }
  | { op: 'set'; field: 'relationshipProfile'; value: StatedProfileSet }
  | { op: 'forget'; field: 'relationshipProfile'; target: { slot: RelationshipProfileSlot } };

export type CardEditErrorCode =
  | 'missing_person_id'
  | 'invalid_edit'
  | 'invalid_relationship'
  | 'invalid_important_date'
  | 'invalid_social_profile'
  | 'invalid_organization'
  | 'invalid_relationship_profile'
  | 'unknown_related_person'
  | 'person_not_found'
  | 'value_not_found'
  | 'too_many_values'
  | 'edit_conflict';

/** A refusal, named on the wire so a card can say WHAT is wrong (the contract's error table). */
export interface CardEditError {
  status: 400 | 404 | 409;
  error: CardEditErrorCode;
  max?: number;
}

/** What the card tells the reader for each code. The `409`s mean "re-read": the card has already been refreshed. */
export const CARD_EDIT_MESSAGE_KEYS: Record<CardEditErrorCode | 'edit_failed', string> = {
  missing_person_id: 'people.card.error.failed',
  invalid_edit: 'people.card.error.failed',
  invalid_relationship: 'people.card.error.invalidRelationship',
  invalid_important_date: 'people.card.error.invalidDate',
  invalid_social_profile: 'people.card.error.invalidSocialProfile',
  invalid_organization: 'people.card.error.invalidOrganization',
  invalid_relationship_profile: 'people.card.error.invalidRelationshipProfile',
  unknown_related_person: 'people.card.error.unknownPerson',
  person_not_found: 'people.card.error.failed',
  value_not_found: 'people.card.error.failed',
  too_many_values: 'people.card.error.full',
  edit_conflict: 'people.card.error.failed',
  edit_failed: 'people.card.error.failed',
};

/** How many times a write re-reads and re-applies after a concurrent write bumped the revision. */
export const CARD_EDIT_ATTEMPTS = 3;

const USER_STATED: Provenance = { source: 'USER', confidence: 'STATED' };
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_KINDS = new Set(['BIRTHDAY', 'ANNIVERSARY', 'MEMORIAL', 'NAME_DAY', 'CUSTOM']);
const HEBREW_MONTHS = new Set([
  'TISHREI',
  'CHESHVAN',
  'KISLEV',
  'TEVET',
  'SHEVAT',
  'ADAR',
  'ADAR_I',
  'ADAR_II',
  'NISAN',
  'IYAR',
  'SIVAN',
  'TAMMUZ',
  'AV',
  'ELUL',
]);
const NETWORKS = new Set(['INSTAGRAM', 'X', 'LINKEDIN', 'FACEBOOK', 'TIKTOK', 'YOUTUBE', 'GITHUB', 'WEBSITE', 'OTHER']);
/** Leap-year month lengths: 29 February is a real birthday. */
const GREGORIAN_MONTH_DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
/** The canonical profile URL for a handle — the same table the phone import and the server use. */
const CANONICAL_URL: Record<string, (handle: string) => string> = {
  INSTAGRAM: (handle) => `https://www.instagram.com/${handle}`,
  X: (handle) => `https://x.com/${handle}`,
  LINKEDIN: (handle) => `https://www.linkedin.com/in/${handle}`,
  FACEBOOK: (handle) => `https://www.facebook.com/${handle}`,
  TIKTOK: (handle) => `https://www.tiktok.com/@${handle}`,
  YOUTUBE: (handle) => `https://www.youtube.com/@${handle}`,
  GITHUB: (handle) => `https://github.com/${handle}`,
};

const record = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
const trimmed = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
const fold = (value: string | undefined) => (value ?? '').normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
const fail = (error: CardEditErrorCode, status: CardEditError['status'] = 400, max?: number): CardEditError => ({
  status,
  error,
  ...(max !== undefined ? { max } : {}),
});
export const isCardEditError = (value: unknown): value is CardEditError =>
  !!record(value) && typeof (value as CardEditError).error === 'string' && typeof (value as CardEditError).status === 'number';

/** A web URL People's WEB_URL format accepts, with `https://` added when the user typed no scheme. */
function webUrl(value: unknown, max = 300): string | undefined | null {
  const raw = trimmed(value);
  if (raw === undefined) return undefined;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    return (url.protocol === 'https:' || url.protocol === 'http:') && url.hostname.includes('.') && withScheme.length <= max && !/\s/.test(raw)
      ? withScheme
      : null;
  } catch {
    return null;
  }
}

function readRelationship(raw: unknown): StoredRelationship | CardEditError {
  const r = record(raw);
  const relation = trimmed(r?.relation);
  if (!relation || relation.length > 200) return fail('invalid_relationship');
  const anchor = r?.relatedToPersonId;
  if (anchor !== undefined && anchor !== null && anchor !== '' && (typeof anchor !== 'string' || !GUID.test(anchor))) {
    return fail('unknown_related_person');
  }
  return { relation, ...(typeof anchor === 'string' && anchor ? { relatedToPersonId: anchor } : {}) };
}

function readImportantDate(raw: unknown): StoredImportantDate | CardEditError {
  const d = record(raw);
  const kind = trimmed(d?.kind)?.toUpperCase();
  const calendar = trimmed(d?.calendar)?.toUpperCase() ?? 'GREGORIAN';
  const day = d?.day;
  if (!kind || !DATE_KINDS.has(kind) || typeof day !== 'number' || !Number.isInteger(day) || day < 1) return fail('invalid_important_date');
  const date: StoredImportantDate = { kind, calendar, day };
  if (calendar === 'GREGORIAN') {
    const month = d?.month;
    if (typeof month !== 'number' || !Number.isInteger(month) || month < 1 || month > 12 || day > GREGORIAN_MONTH_DAYS[month - 1] || d?.hebrewMonth) {
      return fail('invalid_important_date');
    }
    date.month = month;
  } else if (calendar === 'HEBREW') {
    const named = trimmed(d?.hebrewMonth)?.toUpperCase();
    const hebrewMonth = named === 'TAMUZ' ? 'TAMMUZ' : named;
    if (!hebrewMonth || !HEBREW_MONTHS.has(hebrewMonth) || day > 30 || (d?.month !== undefined && d?.month !== null)) {
      return fail('invalid_important_date');
    }
    date.hebrewMonth = hebrewMonth;
  } else {
    return fail('invalid_important_date');
  }
  if (d?.year !== undefined && d?.year !== null) {
    if (typeof d.year !== 'number' || !Number.isInteger(d.year) || d.year < 1 || d.year > 9999) return fail('invalid_important_date');
    date.year = d.year;
  }
  if (d?.label !== undefined && d?.label !== null && d?.label !== '') {
    const label = trimmed(d.label);
    if (!label || label.length > 100) return fail('invalid_important_date');
    date.label = label;
  }
  return date;
}

function readSocialProfile(raw: unknown): StoredSocialProfile | CardEditError {
  const p = record(raw);
  const network = trimmed(p?.network)?.toUpperCase();
  if (!network || !NETWORKS.has(network)) return fail('invalid_social_profile');
  const handle = trimmed(p?.handle)?.replace(/^@+/, '');
  if (handle !== undefined && (!handle || handle.length > 100 || /\s/.test(handle))) return fail('invalid_social_profile');
  const url = webUrl(p?.url);
  if (url === null || (network === 'WEBSITE' && (!url || handle)) || (!handle && !url)) return fail('invalid_social_profile');
  const derived = url ?? (handle && CANONICAL_URL[network] ? CANONICAL_URL[network](handle) : undefined);
  return { network, ...(handle ? { handle } : {}), ...(derived ? { url: derived } : {}) };
}

function readOrganization(raw: unknown): StoredOrganization | CardEditError {
  const o = record(raw);
  const company = trimmed(o?.company);
  const title = trimmed(o?.title);
  const website = webUrl(o?.website);
  if ((company && company.length > 150) || (title && title.length > 150) || website === null || (!company && !title && !website)) {
    return fail('invalid_organization');
  }
  return { ...(company ? { company } : {}), ...(title ? { title } : {}), ...(website ? { website } : {}) };
}

// --- the relationship profile's stated half (plan P2.3) -----------------------------------------------------------

const FEELINGS = new Set(['CLOSE', 'WARM', 'NEUTRAL', 'DISTANT']);
const CLOSENESSES = new Set(['INNER_CIRCLE', 'CLOSE', 'REGULAR', 'PERIPHERAL']);
const REGISTERS = new Set(['INTIMATE', 'CASUAL', 'FRIENDLY_PROFESSIONAL', 'FORMAL']);
/** person.proto's caps on the stated half — the card's input limits and the server's `invalid_relationship_profile`. */
export const RELATIONSHIP_PROFILE_CAPS = { feelingWords: 120, toneNote: 120, address: 60, avoid: 5, avoidItem: 120 } as const;
const ENUMS: Partial<Record<StatedProfileField, Set<string>>> = { feeling: FEELINGS, closeness: CLOSENESSES, register: REGISTERS };
const TEXTS: Partial<Record<StatedProfileField, number>> = {
  feelingWords: RELATIONSHIP_PROFILE_CAPS.feelingWords,
  toneNote: RELATIONSHIP_PROFILE_CAPS.toneNote,
  address: RELATIONSHIP_PROFILE_CAPS.address,
};
const STATED_FIELDS: StatedProfileField[] = ['feeling', 'feelingWords', 'closeness', 'register', 'toneNote', 'address', 'avoid', 'doNotApproach'];
/** The stated fields each row of the section owns: forgetting the feeling takes its words with it, the tone its note. */
export const RELATIONSHIP_PROFILE_SLOTS: Record<RelationshipProfileSlot, StatedProfileField[]> = {
  feeling: ['feeling', 'feelingWords'],
  closeness: ['closeness'],
  tone: ['register', 'toneNote'],
  address: ['address'],
  avoid: ['avoid'],
  doNotApproach: ['doNotApproach'],
};

/**
 * A `set` of the stated profile. Unknown keys (provenance included — the card never chooses it) are ignored; an
 * enum the profile does not know, a part over its cap or more than five avoid topics is refused. An empty string,
 * an empty avoid list or `doNotApproach: false` is a key sent empty, which clears it.
 */
function readRelationshipProfile(raw: unknown): StatedProfileSet | CardEditError {
  const p = record(raw);
  if (!p) return fail('invalid_relationship_profile');
  const set: StatedProfileSet = {};
  for (const field of STATED_FIELDS) {
    if (!(field in p) || p[field] === undefined) continue;
    const value = p[field];
    const allowed = ENUMS[field];
    const cap = TEXTS[field];
    if (allowed) {
      const name = typeof value === 'string' ? value.trim().toUpperCase() : undefined;
      if (!name || !allowed.has(name)) return fail('invalid_relationship_profile');
      set[field] = name;
    } else if (cap !== undefined) {
      if (value !== null && typeof value !== 'string') return fail('invalid_relationship_profile');
      const words = trimmed(value);
      if (words && words.length > cap) return fail('invalid_relationship_profile');
      set[field] = words;
    } else if (field === 'avoid') {
      if (!Array.isArray(value) || value.length > RELATIONSHIP_PROFILE_CAPS.avoid || value.some((item) => typeof item !== 'string')) {
        return fail('invalid_relationship_profile');
      }
      const topics: string[] = [];
      for (const item of value as string[]) {
        const topic = trimmed(item);
        if (topic && topic.length > RELATIONSHIP_PROFILE_CAPS.avoidItem) return fail('invalid_relationship_profile');
        // One topic once: "her ex" twice is still one thing never to bring up.
        if (topic && !topics.some((held) => fold(held) === fold(topic))) topics.push(topic);
      }
      set.avoid = topics.length ? topics : undefined;
    } else {
      if (typeof value !== 'boolean') return fail('invalid_relationship_profile');
      set.doNotApproach = value || undefined;
    }
  }
  return Object.keys(set).length ? set : fail('invalid_relationship_profile');
}

/** A stored stated field as the card compares it: an unset enum, a blank text, `[]` and `false` are all "not set". */
function storedStated(stated: StoredStatedProfile | undefined, field: StatedProfileField): string | string[] | boolean | undefined {
  const value = (stated as Record<string, unknown> | undefined)?.[field];
  if (typeof value === 'string') return value && !value.startsWith('UNKNOWN_') ? value : undefined;
  if (Array.isArray(value)) return value.length ? (value as string[]) : undefined;
  return value === true ? true : undefined;
}

const READERS = {
  relationship: readRelationship,
  importantDate: readImportantDate,
  socialProfile: readSocialProfile,
  organization: readOrganization,
  relationshipProfile: readRelationshipProfile,
} as const;

/**
 * The request's `edit` object, validated. `target` is read leniently — it is a value the card READ from the
 * server and is only ever matched against what is stored, never written — so a stored value the strict reader
 * would now refuse can still be forgotten.
 */
export function parseCardEdit(raw: unknown): CardEdit | CardEditError {
  const e = record(raw);
  const op = e?.op;
  const field = e?.field as CardField;
  if ((op !== 'set' && op !== 'forget') || typeof field !== 'string' || !Object.prototype.hasOwnProperty.call(READERS, field)) {
    return fail('invalid_edit');
  }
  const target = record(e?.target);
  if (op === 'forget') {
    if (field === 'organization') return { op, field };
    if (field === 'relationshipProfile') {
      const slot = target?.slot;
      return typeof slot === 'string' && Object.prototype.hasOwnProperty.call(RELATIONSHIP_PROFILE_SLOTS, slot)
        ? { op, field, target: { slot: slot as RelationshipProfileSlot } }
        : fail('invalid_edit');
    }
    return target ? ({ op, field, target } as CardEdit) : fail('invalid_edit');
  }
  const value = READERS[field](e?.value);
  if (isCardEditError(value)) return value;
  return { op, field, value, ...(target && field !== 'organization' && field !== 'relationshipProfile' ? { target } : {}) } as CardEdit;
}

// --- identity: which stored entry a target names, and which two values are "the same" -------------------------

const relationshipKey = (r: StoredRelationship) => `${fold(r.relation)}\u0000${r.relatedToPersonId ?? ''}`;
const calendarOf = (d: StoredImportantDate) => (d.calendar && d.calendar !== 'UNKNOWN_CALENDAR_SYSTEM' ? d.calendar : 'GREGORIAN');
/** A person has one birthday per calendar: setting one replaces the stored one rather than adding a second. */
const isBirthdayOf = (calendar: string, d: StoredImportantDate) => d.kind === 'BIRTHDAY' && calendarOf(d) === calendar;
const dateKey = (d: StoredImportantDate) => {
  const calendar = calendarOf(d);
  const placement = calendar === 'HEBREW' ? (d.hebrewMonth === 'TAMUZ' ? 'TAMMUZ' : d.hebrewMonth) : d.month;
  return `${d.kind}|${calendar}|${placement}|${d.day}|${d.year ?? ''}|${fold(d.label)}`;
};
const profileKey = (p: StoredSocialProfile) => {
  const handle = fold((p.handle ?? '').replace(/^@+/, ''));
  return handle ? `${p.network}|${handle}` : `${p.network}|url|${fold(p.url).replace(/\/+$/, '')}`;
};

/** A stored value as it may go back on the wire: without what the service computes (`derived`, `provenance.setAt`). */
function writable<V extends object>(value: V): V {
  const { derived: _derived, ...rest } = value as V & { derived?: unknown; provenance?: Record<string, unknown> };
  const provenance = (rest as { provenance?: Record<string, unknown> }).provenance;
  if (!provenance) return rest as V;
  const { setAt: _setAt, ...kept } = provenance;
  return { ...rest, provenance: kept } as V;
}

type ListName = keyof typeof PERSON_CARD_CAPS;
const LIST_OF: Record<Exclude<CardField, 'organization' | 'relationshipProfile'>, ListName> = {
  relationship: 'relationships',
  importantDate: 'importantDates',
  socialProfile: 'socialProfiles',
};
const KEY_OF: Record<ListName, (value: object) => string> = {
  relationships: relationshipKey as (value: object) => string,
  importantDates: dateKey as (value: object) => string,
  socialProfiles: profileKey as (value: object) => string,
};

/**
 * What the edit writes: the ONE field it touches, as its whole new value, and the field mask naming it. The
 * relationship profile is the exception to "whole": it is written by sub-path (`relationshipProfile.stated.<field>`,
 * plus `.provenance`), so only the fields the edit names move and the learned half is never sent.
 */
export interface CardPatch {
  fieldMask: string[];
  values: PersonCardValues;
}

/**
 * A relationship-profile edit laid over the stored stated half. `set` writes the keys it holds; `forget` clears the
 * row's fields. Every path it writes goes with `relationshipProfile.stated.provenance`, stamped USER/STATED — the
 * service stamps `setAt` and, when a write clears the feeling, the tone or the address, leaves the `relprofile:*`
 * tombstone and clears the learned counterpart itself (the tombstone is the diff, as with the lists). `null` when
 * nothing would change.
 */
function applyRelationshipProfileEdit(person: PersonCardValues, edit: Extract<CardEdit, { field: 'relationshipProfile' }>): CardPatch | null {
  const stated = person.relationshipProfile?.stated;
  const next: StatedProfileSet =
    edit.op === 'set' ? edit.value : Object.fromEntries(RELATIONSHIP_PROFILE_SLOTS[edit.target.slot].map((field) => [field, undefined]));
  const fields = (Object.keys(next) as StatedProfileField[]).filter((field) => STATED_FIELDS.includes(field));
  if (fields.every((field) => JSON.stringify(storedStated(stated, field)) === JSON.stringify(next[field]))) return null;
  const written = Object.fromEntries(fields.filter((field) => next[field] !== undefined).map((field) => [field, next[field]]));
  return {
    fieldMask: [...fields, 'provenance'].map((field) => `relationshipProfile.stated.${field}`),
    values: { relationshipProfile: { stated: { ...written, provenance: USER_STATED } as StoredStatedProfile } },
  };
}

/**
 * The edit laid over the stored person. `null` when it changes nothing (re-saving the same value), so the caller
 * writes nothing and the card still gets its fresh entry back.
 *
 * - `set` with a `target` EDITS that entry in place (409 `value_not_found` when the card's copy is stale);
 *   without one it ADDS — except that an identical value already stored is replaced rather than doubled, and a
 *   BIRTHDAY replaces the stored birthday of the same calendar (a person has one).
 * - `forget` removes the target entry (or clears the organization).
 * - Every value this writes carries provenance USER/STATED; every other entry goes back exactly as stored.
 */
export function applyCardEdit(person: PersonCardValues, edit: CardEdit): CardPatch | null | CardEditError {
  if (edit.field === 'relationshipProfile') return applyRelationshipProfileEdit(person, edit);
  if (edit.field === 'organization') {
    if (edit.op === 'forget') return person.organization ? { fieldMask: ['organization'], values: { organization: undefined } } : null;
    const next = { ...edit.value, provenance: USER_STATED };
    const stored = person.organization ? writable(person.organization) : undefined;
    return JSON.stringify(stored) === JSON.stringify(next) ? null : { fieldMask: ['organization'], values: { organization: next } };
  }

  const list = LIST_OF[edit.field];
  const key = KEY_OF[list];
  const stored = ((person[list] ?? []) as object[]).map((value) => writable(value));
  const next = [...stored];

  const targetAt = edit.target ? next.findIndex((held) => key(held) === key(edit.target!)) : -1;
  if (edit.target && targetAt < 0) return fail('value_not_found', 409);

  if (edit.op === 'forget') {
    next.splice(targetAt, 1);
  } else {
    const value = { ...edit.value, provenance: USER_STATED };
    const birthdayCalendar = edit.field === 'importantDate' && isBirthdayOf(calendarOf(edit.value), edit.value) ? calendarOf(edit.value) : undefined;
    const same = (held: object) =>
      key(held) === key(value) || (birthdayCalendar !== undefined && isBirthdayOf(birthdayCalendar, held as StoredImportantDate));
    if (targetAt >= 0) {
      next[targetAt] = value;
      // The edited value may now be the same as ANOTHER entry (or a second birthday); the list keeps one.
      for (let i = next.length - 1; i >= 0; i--) {
        if (i !== targetAt && same(next[i])) next.splice(i, 1);
      }
    } else {
      const at = next.findIndex(same);
      if (at >= 0) next[at] = value;
      else next.push(value);
    }
    if (next.length > PERSON_CARD_CAPS[list]) return fail('too_many_values', 409, PERSON_CARD_CAPS[list]);
  }

  return JSON.stringify(next) === JSON.stringify(stored) ? null : { fieldMask: [list], values: { [list]: next } as PersonCardValues };
}

/**
 * True when a revision-carrying write lost the optimistic-concurrency race (common-lib `isRevisionConflictError`,
 * restated because this module may not import common-lib): SDL answers ALREADY_EXISTS "Outdated revision", some
 * paths FAILED_PRECONDITION — the People service's own `concurrent_write` among them.
 */
export function isRevisionConflict(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (typeof message === 'string' && message.includes('Outdated revision')) return true;
  return code === 6 || code === 'ALREADY_EXISTS' || code === 9 || code === 'FAILED_PRECONDITION';
}

/** The two People calls the edit needs, both under the CALLER's aspects (bound by the route). */
export interface CardPeopleStore {
  /**
   * The caller's own person — null when the caller's site holds no such person (NOT_FOUND included). With
   * `relationshipProfile`, the one GetPerson that carries the profile (every other read strips it): a
   * relationship-profile edit needs the stored stated half to know whether it changes anything.
   */
  getPerson(personId: string, read?: { relationshipProfile?: boolean }): Promise<StoredPerson | null>;
  updatePerson(person: PersonCardValues & { id: string; revision?: string }, fieldMask: string[]): Promise<StoredPerson>;
}

/**
 * Validates the anchor, then reads, applies and writes under the revision guard, re-reading on a lost race. The
 * result is the person as stored after the edit (or as found, when the edit changed nothing).
 */
export async function runCardEdit(
  store: CardPeopleStore,
  personId: string,
  edit: CardEdit,
): Promise<{ person: StoredPerson; changed: boolean } | CardEditError> {
  const anchor = edit.op === 'set' && edit.field === 'relationship' ? edit.value.relatedToPersonId : undefined;
  if (anchor) {
    // "Yoni's teacher" must point at a person THIS caller holds; another site's id answers like a made-up one.
    if (anchor === personId || !(await store.getPerson(anchor).catch(() => null))) return fail('unknown_related_person');
  }

  const read = edit.field === 'relationshipProfile' ? ([{ relationshipProfile: true }] as const) : ([] as const);
  for (let attempt = 0; attempt < CARD_EDIT_ATTEMPTS; attempt++) {
    const person = await store.getPerson(personId, ...read);
    if (!person?.id) return fail('person_not_found', 404);
    const patch = applyCardEdit(person, edit);
    if (patch === null) return { person, changed: false };
    if (isCardEditError(patch)) return patch;
    try {
      const updated = await store.updatePerson({ id: person.id, revision: person.revision, ...patch.values }, patch.fieldMask);
      return { person: updated, changed: true };
    } catch (error) {
      if (!isRevisionConflict(error)) throw error;
    }
  }
  return fail('edit_conflict', 409);
}

/**
 * The People wire enums (DateKind, CalendarSystem, HebrewMonth, SocialNetwork, Provenance.Source, FactConfidence)
 * are TypeScript STRING enums, which are nominal: a validated 'BIRTHDAY' IS the enum's value at runtime but not its
 * type. `parseCardEdit` has already checked every enum field it writes against the service's own value list (and
 * the route test pins the request), so this is the one place those strings take the generated type — the request
 * object around them stays fully typed.
 */
export function asPeopleEnums<T>(values: PersonCardValues & { id: string; revision?: string }): T {
  return values as unknown as T;
}
